import dayjs from "dayjs";
import {
  OfferDiscountType,
  OfferSelectionType,
  OfferTargetType,
} from "../constants/offers.constants";
import { Offer, OfferDraft, OfferFormValidation } from "../entities/offer.entity";
import { isOfferScheduled } from "./offer-status.utils";
import { isValidDiscountValue } from "./offer-discount.utils";

export const validateOfferForm = (offer: OfferDraft): OfferFormValidation => {
  const isValidName = Boolean(offer.name?.trim());
  const hasProducts =
    offer.offerSelectionType === OfferSelectionType.Categories
      ? Boolean(offer.categories?.length)
      : Boolean(offer.products?.length);

  let isOfferDateValid = true;
  let isValidStartDate = true;
  let isValidEndDate = true;

  if (offer.scheduled && offer.startDate && offer.endDate) {
    const start = dayjs(offer.startDate);
    const end = dayjs(offer.endDate);
    const isValidEndDateOrder = end.isAfter(start) || end.isSame(start, "day");
    isValidStartDate = start.isAfter(dayjs().subtract(1, "day"));
    isValidEndDate = isValidEndDateOrder;
    isOfferDateValid = isValidStartDate && isValidEndDate;
  } else if (offer.scheduled) {
    isOfferDateValid = false;
    isValidStartDate = Boolean(offer.startDate);
    isValidEndDate = Boolean(offer.endDate);
  }

  const isValidProducts = hasProducts;
  const isValidOffer = isValidName && isValidProducts && isOfferDateValid;

  return {
    isValidName,
    isValidStartDate,
    isValidEndDate,
    isValidProducts,
    isValidOffer,
  };
};

export const validateOfferDiscounts = (offer: OfferDraft): boolean => {
  if (
    offer.targetType === OfferTargetType.AllProducts ||
    offer.targetType === OfferTargetType.AllCategories
  ) {
    if (!offer.value || offer.value <= 0) return false;
    if (offer.discountType === OfferDiscountType.Percentage) {
      return offer.value < 100;
    }
    const prices = (offer.products || []).map((p) => p.price);
    const minPrice = prices.length ? Math.min(...prices) : undefined;
    return minPrice != null ? offer.value < minPrice : true;
  }

  if (offer.offerSelectionType === OfferSelectionType.Categories) {
    return Boolean(
      offer.categories?.every(
        (c) => c.offerValue > 0 && c.offerValue < 100
      )
    );
  }

  return Boolean(
    offer.products?.every((p) =>
      isValidDiscountValue(p.offerValue, p.typeDiscount, p.price)
    )
  );
};

export const isWizardStepValid = (
  step: "selector" | "discount" | "form",
  offer: OfferDraft
): boolean => {
  switch (step) {
    case "selector":
      return offer.offerSelectionType === OfferSelectionType.Categories
        ? Boolean(offer.categories?.length)
        : Boolean(offer.products?.length);
    case "discount":
      return validateOfferDiscounts(offer);
    case "form":
      return validateOfferForm(offer).isValidOffer;
    default:
      return false;
  }
};

export const createEmptyDraft = (): OfferDraft => ({
  name: "",
  description: "",
  products: [],
  categories: [],
  discountType: OfferDiscountType.Percentage,
  offerSelectionType: OfferSelectionType.Products,
  targetType: OfferTargetType.ProductsIds,
  scheduled: false,
  enabled: true,
  tags: [],
});

export const offerToDraft = (offer: Offer): OfferDraft => ({
  ...offer,
  scheduled: isOfferScheduled(offer.startDate, offer.endDate),
});
